/* eslint-disable ember/no-classic-components, ember/no-computed-properties-in-native-classes */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { computed } from '@ember/object';
import { hasText, sectionComponentText } from 'linced-app/utils/reporting';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import { tagName } from '@ember-decorators/component';

@tagName('')
export default class ReportCompletionStatus extends Component {
  // passed in
  @tracked sectionMap = null;

  @tracked studentId = null;
  @tracked templateSectionId = null;
  textComponents = null;
  progressComponents = null;
  rubricElements = null;
  onMarkComplete = null;

  @computed('progressComponents.[]')
  get hasEffort() {
    return (this.progressComponents || []).some((c) => c.type === 'effort');
  }

  @computed('progressComponents.[]')
  get hasStrandVisual() {
    return (this.progressComponents || []).some(
      (c) => c.type === 'strand-visual',
    );
  }

  @computed('progressComponents.[]')
  get hasProgressDescriptor() {
    return (this.progressComponents || []).some(
      (c) => c.type === 'progress-descriptor',
    );
  }

  // The JUDGEMENTS card only renders when one of these is present
  // (see progress-panel.hbs). If neither is, the judgement panel must
  // not contribute to the total/completed counts — otherwise the section
  // can never reach full completion (e.g. an effort-only section).
  @computed('hasStrandVisual', 'hasProgressDescriptor')
  get hasJudgementPanel() {
    return this.hasStrandVisual || this.hasProgressDescriptor;
  }

  @computed('progressComponents.[]')
  get effortPerElement() {
    let strandVisual = (this.progressComponents || []).find(
      (c) => c.type === 'strand-visual',
    );
    return !!(strandVisual && strandVisual.visualType === 'table-effort');
  }

  @computed('sectionMap', 'studentId', 'templateSectionId')
  get section() {
    let map = this.sectionMap;
    if (!map) return null;
    let studentSections = map[this.studentId];
    if (!studentSections) return null;
    return studentSections[this.templateSectionId] || null;
  }

  @computed(
    'textComponents.[]',
    'progressComponents.[]',
    'hasEffort',
    'hasJudgementPanel',
  )
  get total() {
    let count = this.textComponents ? this.textComponents.length : 0;

    // Judgement panel counts as 1, but only when its UI is rendered.
    if (this.hasJudgementPanel) {
      count++;
    }
    // Effort panel counts as 1 when present.
    if (this.hasEffort) {
      count++;
    }

    return count;
  }

  @computed(
    'section.{narrative,nextSteps,nextStepsGoals,homeHelp,judgement,effort}',
    'section.elementProgressions.@each.{elementId,judgement,effort}',
    'textComponents.[]',
    'rubricElements.[]',
    'hasEffort',
    'hasStrandVisual',
    'hasJudgementPanel',
    'effortPerElement',
  )
  get completed() {
    let section = this.section;
    if (!section) return 0;

    let count = 0;

    // Text components
    if (this.textComponents) {
      this.textComponents.forEach((comp) => {
        if (hasText(sectionComponentText(section, comp.type))) {
          count++;
        }
      });
    }

    let elements = this.rubricElements || [];
    let sectionElements = section.get('elementProgressions') || [];

    // Judgement panel — only count when its UI is rendered. Overall must
    // be set; per-element judgements are only required when strand-visual
    // is present (the only UI that exposes per-element entry). With
    // progress-descriptor alone the user has no way to set per-element
    // values, so requiring them would leave the section permanently
    // incomplete.
    if (this.hasJudgementPanel) {
      let judgementComplete = !!section.get('judgement');
      if (judgementComplete && this.hasStrandVisual && elements.length) {
        judgementComplete = elements.every((el) => {
          let entry = findBy(sectionElements, 'elementId', el.get('id'));
          return entry && entry.get('judgement');
        });
      }
      if (judgementComplete) {
        count++;
      }
    }

    // Effort panel — overall must be set; per-element effort is only
    // required when effortPerElement (strand-visual with table-effort).
    if (this.hasEffort) {
      let effortComplete = !!section.get('effort');
      if (effortComplete && this.effortPerElement && elements.length) {
        effortComplete = elements.every((el) => {
          let entry = findBy(sectionElements, 'elementId', el.get('id'));
          return entry && entry.get('effort');
        });
      }
      if (effortComplete) {
        count++;
      }
    }

    return count;
  }

  // True when nothing remains to complete. Sections whose only components
  // are non-counted types (e.g. assessment) have total === 0; we treat
  // those as trivially complete so the teacher can still mark the section.
  @computed('completed', 'total')
  get allComplete() {
    return this.completed >= this.total;
  }

  @computed(
    'section.{narrative,nextSteps,nextStepsGoals,homeHelp}',
    'textComponents.[]',
  )
  get textIsComplete() {
    let section = this.section;
    if (!section) return false;
    let components = this.textComponents || [];
    if (!components.length) return false;
    return components.every((comp) =>
      hasText(sectionComponentText(section, comp.type)),
    );
  }

  @computed('section.complete')
  get isComplete() {
    return this.section && this.section.get('complete');
  }
}
